// DocumentModals.js - Modaler för fliken Dokument (Spår D):
// ladda upp, skriv nytt från mall, redigera utkast, visa/skriv ut/makulera.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TextAreaWithPhrases from '../TextAreaWithPhrases';
import {
  ACCEPTED_UPLOAD_TYPES, MAX_UPLOAD_MB, CATEGORY_LABELS, KIND_LABELS, SOURCE_LABELS,
  uploadDocument, createWrittenDocument, getDocument, updateDocument, signDocument,
  voidDocument, fetchDocumentBlob, getDocumentTemplates, getDocumentAuditLog, errorMessage,
} from './documentsApi';

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

export const todayIso = () => new Date().toLocaleDateString('sv-SE');

export const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('sv-SE')} ${d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** Skriv ut via en dold iframe (PDF och bild) — webbläsarens utskriftsdialog. */
const printBlob = (blob) => {
  const url = window.URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (e) {
      // Vissa webbläsare blockerar print() i PDF-visaren - öppna i ny flik i stället
      window.open(url, '_blank', 'noopener');
    }
    setTimeout(() => {
      document.body.removeChild(frame);
      window.URL.revokeObjectURL(url);
    }, 60000);
  };
  document.body.appendChild(frame);
};

export const documentFilename = (doc) => {
  if (doc.original_filename) return doc.original_filename;
  return `${doc.title || 'dokument'}${doc.status === 'draft' ? ' (utkast)' : ''}.pdf`;
};

/** Bokningar att välja bland (ej avbokade, senaste först). */
const useAppointmentOptions = (patient) => useMemo(() => (patient?.appointments || [])
  .filter(a => a.status !== 'cancelled')
  .slice()
  .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
  .map(a => ({
    value: a.appointment_id,
    label: `${formatDateTime(a.start_time)}${a.appointment_type?.name ? ` · ${a.appointment_type.name}` : ''}`
      + `${a.user_name ? ` · ${a.user_name}` : ''}`,
  })), [patient]);

const Modal = ({ title, icon, onClose, size = 'md', children, footer, closeOnOverlay = true }) => (
  <div className="doc-modal-overlay" onMouseDown={closeOnOverlay ? onClose : undefined}>
    <div className={`doc-modal doc-modal-${size}`} onMouseDown={e => e.stopPropagation()}
      role="dialog" aria-modal="true" aria-label={title}>
      <div className="doc-modal-header">
        <h3>{icon && <i className={icon}></i>} {title}</h3>
        <button type="button" className="doc-close" onClick={onClose} aria-label="Stäng">
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="doc-modal-body">{children}</div>
      {footer && <div className="doc-modal-footer">{footer}</div>}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Ladda upp / skanna in
// ─────────────────────────────────────────────────────────────────────────────
export const UploadDocumentModal = ({ patient, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [documentDate, setDocumentDate] = useState(todayIso());
  const [appointmentId, setAppointmentId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const appointmentOptions = useAppointmentOptions(patient);

  const pickFile = (f) => {
    setError('');
    if (!f) return;
    if (f.type && !ALLOWED_MIME.includes(f.type)) {
      const heic = /heic|heif/i.test(f.type) || /\.(heic|heif)$/i.test(f.name);
      setError(heic
        ? 'HEIC-bilder (t.ex. från iPhone) stöds inte. Spara bilden som JPG eller PDF och försök igen.'
        : 'Filtypen stöds inte. Tillåtna filtyper: PDF, JPG och PNG.');
      return;
    }
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`Filen är för stor (${formatSize(f.size)}). Max ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setFile(f);
    if (!titleTouched) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Välj en fil'); return; }
    try {
      setSaving(true);
      setError('');
      const doc = await uploadDocument(patient.patient_id, {
        file, title: title.trim(), documentDate, appointmentId,
      });
      toast.success('Dokumentet är uppladdat');
      onUploaded(doc);
    } catch (err) {
      setError(await errorMessage(err, 'Kunde inte ladda upp dokumentet'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Ladda upp dokument" icon="fas fa-upload" onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Avbryt</button>
          <button type="submit" form="doc-upload-form" className="btn btn-primary" disabled={saving || !file}>
            {saving ? <><i className="fas fa-spinner fa-spin"></i> Laddar upp…</> : 'Ladda upp'}
          </button>
        </>
      )}>
      <form id="doc-upload-form" onSubmit={handleSubmit}>
        <div
          className={`doc-dropzone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
          role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        >
          <input ref={inputRef} type="file" accept={ACCEPTED_UPLOAD_TYPES} hidden
            onChange={e => pickFile(e.target.files?.[0])} data-testid="doc-file-input" />
          {file ? (
            <>
              <i className={`fas ${file.type === 'application/pdf' ? 'fa-file-pdf' : 'fa-file-image'}`}></i>
              <strong>{file.name}</strong>
              <span>{formatSize(file.size)} · klicka för att byta fil</span>
            </>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt"></i>
              <strong>Välj fil eller dra hit</strong>
              <span>PDF, JPG eller PNG, max {MAX_UPLOAD_MB} MB. Inscannade dokument: spara som PDF i skannern.</span>
            </>
          )}
        </div>

        <div className="doc-field">
          <label htmlFor="doc-upload-title">Titel</label>
          <input id="doc-upload-title" type="text" value={title} maxLength={255}
            onChange={e => { setTitle(e.target.value); setTitleTouched(true); }}
            placeholder="T.ex. Remissvar röntgen" />
        </div>
        <div className="doc-field-row">
          <div className="doc-field">
            <label htmlFor="doc-upload-date">Dokumentets datum</label>
            <input id="doc-upload-date" type="date" value={documentDate} max={todayIso()}
              onChange={e => setDocumentDate(e.target.value)} required />
          </div>
          <div className="doc-field">
            <label htmlFor="doc-upload-appt">Koppla till bokning (valfritt)</label>
            <select id="doc-upload-appt" value={appointmentId} onChange={e => setAppointmentId(e.target.value)}>
              <option value="">Ingen bokning</option>
              {appointmentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <p className="doc-hint">
          <i className="fas fa-lock"></i> Ett uppladdat dokument låses direkt och kan inte ändras — fel dokument makuleras.
        </p>
        {error && <div className="doc-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      </form>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Skriv nytt dokument - välj mall
// ─────────────────────────────────────────────────────────────────────────────
export const NewWrittenDocumentModal = ({ patient, onClose, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [documentDate, setDocumentDate] = useState(todayIso());
  const [appointmentId, setAppointmentId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const appointmentOptions = useAppointmentOptions(patient);

  useEffect(() => {
    getDocumentTemplates()
      .then(data => setTemplates(data.templates || []))
      .catch(err => {
        console.error('Kunde inte hämta dokumentmallar:', err);
        setError('Kunde inte hämta dokumentmallar — du kan skriva ett tomt dokument.');
      })
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    templates.forEach(t => {
      const key = t.category_label || CATEGORY_LABELS[t.category] || 'Övrigt';
      (groups[key] = groups[key] || []).push(t);
    });
    return groups;
  }, [templates]);

  const selectTemplate = (value) => {
    setTemplateId(value);
    const t = templates.find(x => String(x.template_id) === value);
    setTitle(t ? t.name : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const doc = await createWrittenDocument(patient.patient_id, {
        template_id: templateId ? Number(templateId) : null,
        title: title.trim() || undefined,
        document_date: documentDate,
        appointment_id: appointmentId ? Number(appointmentId) : null,
      });
      onCreated(doc);
    } catch (err) {
      setError(await errorMessage(err, 'Kunde inte skapa dokumentet'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Skriv nytt dokument" icon="fas fa-file-signature" onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Avbryt</button>
          <button type="submit" form="doc-new-form" className="btn btn-primary" disabled={saving || loading}>
            {saving ? <><i className="fas fa-spinner fa-spin"></i> Skapar…</> : 'Skapa utkast'}
          </button>
        </>
      )}>
      <form id="doc-new-form" onSubmit={handleSubmit}>
        <div className="doc-field">
          <label htmlFor="doc-new-template">Dokumentmall</label>
          <select id="doc-new-template" value={templateId} onChange={e => selectTemplate(e.target.value)} disabled={loading}>
            <option value="">{loading ? 'Hämtar mallar…' : 'Tomt dokument (utan mall)'}</option>
            {Object.entries(grouped).map(([group, list]) => (
              <optgroup key={group} label={group}>
                {list.map(t => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name}{t.is_global ? '' : ' (klinikens)'}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="doc-field">
          <label htmlFor="doc-new-title">Titel</label>
          <input id="doc-new-title" type="text" value={title} maxLength={255}
            onChange={e => setTitle(e.target.value)} placeholder="T.ex. Intyg om besök" />
        </div>
        <div className="doc-field-row">
          <div className="doc-field">
            <label htmlFor="doc-new-date">Dokumentets datum</label>
            <input id="doc-new-date" type="date" value={documentDate} onChange={e => setDocumentDate(e.target.value)} required />
          </div>
          <div className="doc-field">
            <label htmlFor="doc-new-appt">Koppla till bokning (valfritt)</label>
            <select id="doc-new-appt" value={appointmentId} onChange={e => setAppointmentId(e.target.value)}>
              <option value="">Ingen bokning</option>
              {appointmentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <p className="doc-hint">
          Mallens variabler (patient, behandlare, klinik, datum) fylls i automatiskt. Dokumentet sparas som
          utkast tills du signerar det.
        </p>
        {error && <div className="doc-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      </form>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Redigera utkast → spara / signera
// ─────────────────────────────────────────────────────────────────────────────
export const DocumentEditorModal = ({ documentId, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [version, setVersion] = useState(1);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = async () => {
    try {
      const d = await getDocument(documentId);
      setDoc(d);
      setTitle(d.title || '');
      setContent(d.content || '');
      setDocumentDate(d.document_date || todayIso());
      setVersion(d.version || 1);
      setDirty(false);
    } catch (err) {
      setError(await errorMessage(err, 'Kunde inte hämta dokumentet'));
    }
  };

  useEffect(() => { load(); }, [documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const payload = () => ({ title, content, document_date: documentDate, expected_version: version });

  const handleConflict = async (err) => {
    if (err?.response?.status === 409 && err.response.data?.conflict) {
      if (window.confirm('Utkastet har ändrats någon annanstans. Ladda om den sparade versionen? (Dina osparade ändringar försvinner.)')) {
        await load();
      }
      return true;
    }
    return false;
  };

  const save = async ({ quiet = false } = {}) => {
    if (!dirty) return true;
    try {
      setBusy(true);
      setError('');
      const d = await updateDocument(documentId, payload());
      setDoc(d);
      setVersion(d.version);
      setDirty(false);
      if (!quiet) toast.success('Utkastet är sparat');
      onChanged?.();
      return true;
    } catch (err) {
      if (!(await handleConflict(err))) setError(await errorMessage(err, 'Kunde inte spara utkastet'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const preview = async () => {
    if (!(await save({ quiet: true }))) return;
    try {
      const blob = await fetchDocumentBlob(documentId);
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      setError(await errorMessage(err, 'Kunde inte förhandsvisa'));
    }
  };

  const sign = async () => {
    if (!content.trim()) { setError('Skriv något innehåll innan du signerar.'); return; }
    if (!window.confirm('Signera dokumentet? Det låses och kan inte ändras efteråt (fel dokument makuleras).')) return;
    try {
      setBusy(true);
      setError('');
      await signDocument(documentId, payload());
      toast.success('Dokumentet är signerat');
      onChanged?.();
      onClose({ signedId: documentId });
    } catch (err) {
      if (!(await handleConflict(err))) setError(await errorMessage(err, 'Kunde inte signera dokumentet'));
    } finally {
      setBusy(false);
    }
  };

  // "Stäng" sparar sista ändringarna och stänger (som journalen)
  const close = async () => {
    if (dirty && !(await save({ quiet: true }))) {
      if (!window.confirm('Utkastet kunde inte sparas. Stänga ändå och förlora ändringarna?')) return;
    }
    onClose();
  };

  const edit = (setter) => (e) => { setter(e.target.value); setDirty(true); };

  return (
    <Modal title={doc ? `Utkast: ${doc.title}` : 'Utkast'} icon="fas fa-pen" onClose={close} size="xl"
      closeOnOverlay={false}
      footer={(
        <>
          <span className="doc-footer-status">
            {busy ? <><i className="fas fa-spinner fa-spin"></i> Sparar…</> : dirty ? 'Osparade ändringar' : 'Sparat'}
          </span>
          <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>Stäng</button>
          <button type="button" className="btn btn-secondary" onClick={preview} disabled={busy || !doc}>
            <i className="fas fa-eye"></i> Förhandsvisa
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => save()} disabled={busy || !dirty}>
            <i className="fas fa-save"></i> Spara utkast
          </button>
          <button type="button" className="btn btn-success" onClick={sign} disabled={busy || !doc}>
            <i className="fas fa-signature"></i> Signera
          </button>
        </>
      )}>
      {!doc && !error && <div className="loading-state"><i className="fas fa-spinner fa-spin"></i> Laddar…</div>}
      {doc && (
        <div className={`doc-editor ${previewUrl ? 'with-preview' : ''}`}>
          <div className="doc-editor-form">
            <div className="doc-field-row">
              <div className="doc-field doc-field-grow">
                <label htmlFor="doc-edit-title">Titel</label>
                <input id="doc-edit-title" type="text" value={title} maxLength={255} onChange={edit(setTitle)} />
              </div>
              <div className="doc-field">
                <label htmlFor="doc-edit-date">Datum</label>
                <input id="doc-edit-date" type="date" value={documentDate} onChange={edit(setDocumentDate)} />
              </div>
            </div>
            {doc.template_name && <p className="doc-hint">Mall: {doc.template_name}</p>}
            <div className="doc-field">
              <label htmlFor="doc-edit-content">Text</label>
              <TextAreaWithPhrases id="doc-edit-content" value={content} onChange={edit(setContent)}
                rows={20} className="doc-textarea" placeholder="Skriv dokumentets text… (; för snabbfraser)" />
            </div>
          </div>
          {previewUrl && (
            <div className="doc-editor-preview">
              <div className="doc-preview-bar">
                <span>Förhandsvisning (utkast)</span>
                <button type="button" className="doc-link-btn" onClick={() => setPreviewUrl(null)}>Dölj</button>
              </div>
              <iframe title="Förhandsvisning" src={previewUrl} className="doc-preview-frame" />
            </div>
          )}
        </div>
      )}
      {error && <div className="doc-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Visa dokument - förhandsvisning, ladda ner, skriv ut, makulera, historik
// ─────────────────────────────────────────────────────────────────────────────
export const DocumentViewerModal = ({ documentId, onClose, onChanged, onEdit, onCopy }) => {
  const [doc, setDoc] = useState(null);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const d = await getDocument(documentId);
        if (cancelled) return;
        setDoc(d);
        const b = await fetchDocumentBlob(documentId);
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(b);
        setBlob(b);
        setUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setError(await errorMessage(err, 'Kunde inte hämta dokumentet'));
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  const download = async () => {
    try {
      // Eget anrop med download=1 så att nedladdningen loggas som nedladdning
      downloadBlob(await fetchDocumentBlob(documentId, { download: true }), documentFilename(doc));
    } catch (err) {
      toast.error(await errorMessage(err, 'Kunde inte ladda ner dokumentet'));
    }
  };

  const submitVoid = async () => {
    if (voidReason.trim().length < 3) { setError('Ange orsak till makuleringen.'); return; }
    try {
      setBusy(true);
      setError('');
      const d = await voidDocument(documentId, voidReason.trim());
      setDoc(d);
      setVoiding(false);
      toast.success('Dokumentet är makulerat');
      onChanged?.();
    } catch (err) {
      setError(await errorMessage(err, 'Kunde inte makulera dokumentet'));
    } finally {
      setBusy(false);
    }
  };

  const toggleHistory = async () => {
    if (history) { setHistory(null); return; }
    try {
      setHistory((await getDocumentAuditLog(documentId)).entries || []);
    } catch (err) {
      toast.error(await errorMessage(err, 'Kunde inte hämta historiken'));
    }
  };

  const isImage = doc?.mime_type?.startsWith('image/');

  return (
    <Modal title={doc?.title || 'Dokument'} icon="fas fa-file-alt" onClose={onClose} size="xl"
      footer={doc && (
        <>
          {doc.can_void && !voiding && (
            <button type="button" className="btn btn-outline-danger doc-footer-left" onClick={() => setVoiding(true)}>
              <i className="fas fa-ban"></i> Makulera
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={toggleHistory}>
            <i className="fas fa-history"></i> {history ? 'Dölj historik' : 'Historik'}
          </button>
          {doc.kind === 'written' && doc.status === 'signed' && onCopy && (
            <button type="button" className="btn btn-secondary" onClick={() => onCopy(doc)}>
              <i className="fas fa-copy"></i> Kopiera till nytt
            </button>
          )}
          {doc.can_edit && onEdit && (
            <button type="button" className="btn btn-secondary" onClick={() => onEdit(doc)}>
              <i className="fas fa-pen"></i> Redigera
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => blob && printBlob(blob)} disabled={!blob}>
            <i className="fas fa-print"></i> Skriv ut
          </button>
          <button type="button" className="btn btn-primary" onClick={download} disabled={!blob}>
            <i className="fas fa-download"></i> Ladda ner
          </button>
        </>
      )}>
      {!doc && !error && <div className="loading-state"><i className="fas fa-spinner fa-spin"></i> Laddar…</div>}
      {doc && (
        <div className="doc-viewer">
          <div className="doc-viewer-meta">
            <DocumentStatusBadge doc={doc} />
            <span><i className="fas fa-calendar"></i> {doc.document_date}</span>
            <span>{KIND_LABELS[doc.kind]}{doc.template_name ? ` · ${doc.template_name}` : ` · ${SOURCE_LABELS[doc.source] || doc.source}`}</span>
            <span><i className="fas fa-user"></i> {doc.created_by_name}</span>
            {doc.status === 'signed' && doc.signed_at && (
              <span title={doc.sha256 ? `SHA-256: ${doc.sha256}` : undefined}>
                <i className="fas fa-lock"></i> {doc.kind === 'upload' ? 'Uppladdat' : 'Signerat'} av {doc.signed_by_name} {formatDateTime(doc.signed_at)}
              </span>
            )}
            {doc.file_size != null && <span>{formatSize(doc.file_size)}</span>}
          </div>

          {doc.voided && (
            <div className="doc-voided-box">
              <strong><i className="fas fa-ban"></i> Makulerat</strong> av {doc.voided_by_name} {formatDateTime(doc.voided_at)}
              <div>Orsak: {doc.void_reason}</div>
            </div>
          )}

          {voiding && (
            <div className="doc-void-form">
              <label htmlFor="doc-void-reason">Orsak till makulering</label>
              <textarea id="doc-void-reason" rows={2} value={voidReason} onChange={e => setVoidReason(e.target.value)}
                placeholder="T.ex. Fel patient, dubblett, fel dokument" autoFocus />
              <div className="doc-void-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setVoiding(false); setError(''); }}>Avbryt</button>
                <button type="button" className="btn btn-danger btn-sm" onClick={submitVoid} disabled={busy}>
                  {busy ? 'Makulerar…' : 'Makulera dokumentet'}
                </button>
              </div>
              <p className="doc-hint">Dokumentet raderas inte — det döljs i listan och finns kvar i historiken.</p>
            </div>
          )}

          {history && (
            <table className="doc-history">
              <tbody>
                {history.map(h => (
                  <tr key={h.log_id}>
                    <td>{formatDateTime(h.timestamp)}</td>
                    <td>{h.action_label}</td>
                    <td>{h.user_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="doc-viewer-preview">
            {!url && !error && <div className="loading-state"><i className="fas fa-spinner fa-spin"></i> Laddar…</div>}
            {url && (isImage
              ? <img src={url} alt={doc.title} className="doc-preview-image" />
              : <iframe title={doc.title} src={url} className="doc-preview-frame" />)}
          </div>
        </div>
      )}
      {error && <div className="doc-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
    </Modal>
  );
};

export const DocumentStatusBadge = ({ doc }) => {
  if (doc.voided) return <span className="doc-badge doc-badge-voided"><i className="fas fa-ban"></i> Makulerat</span>;
  if (doc.status === 'draft') return <span className="doc-badge doc-badge-draft"><i className="fas fa-pen"></i> Utkast</span>;
  return <span className="doc-badge doc-badge-signed"><i className="fas fa-lock"></i> {doc.kind === 'upload' ? 'Låst' : 'Signerat'}</span>;
};
